import { Component, EventEmitter, Input, OnChanges, Optional, Output, Self, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { IdName } from '@api/models/idname';

@Component({
  selector: 'flow-select-dropdown-with-status-toggle',
  templateUrl: './select-dropdown-with-status-toggle.component.html',
  styleUrls: ['./select-dropdown-with-status-toggle.component.scss'],
})
export class SelectDropdownWithStatusToggleComponent implements ControlValueAccessor, OnChanges {
  @Input() label: string = '';
  @Input() options: IdName[] = [];
  @Input() multiple: boolean = true;
  @Input() isDisabled: boolean = false;
  @Input() hasSelectAll: boolean = false;
  @Input() hasDeletedDisabledDataToggle = false;
  @Input() includeDeletedDataLabel: string = '';
  @Input() isRequired: boolean = false;
  @Input() placeholder: string = '';

  @Output() selectionChange = new EventEmitter<number[]>();

  value: number[] = [];
  selectAllClicked: boolean = false;
  deletedDisabledOptionId: number = -1;

  constructor(@Optional() @Self() protected control: NgControl) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(value: number[] | null): void {
    this.value = value ?? [];
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options && this.selectAllClicked) {
      const newOptionIds = this.options.map(o => o.id);
      const currentIds = new Set(this.value);

      for (const id of newOptionIds) {
        currentIds.add(id);
      }

      this.value = Array.from(currentIds);
      this.selectAllClicked = false;
    }
  }

  onSelectionChange(selected: number[]): void {
    this.value = selected;
    this.emitChanges();
  }

  toggleSelectAll(): void {
    const optionIds = this.options.map(o => o.id);
    const fullSelection = this.hasDeletedDisabledDataToggle ? [this.deletedDisabledOptionId, ...optionIds] : optionIds;

    const isAllSelected = fullSelection.every(id => this.value.includes(id));

    if (isAllSelected) {
      this.value = [];
      this.selectAllClicked = false;
    } else {
      this.selectAllClicked = true;
      this.value = fullSelection;
    }

    this.emitChanges();
  }

  get selectedAll(): boolean {
    if (!this.options || this.options.length === 0) {
      return false;
    }

    const allOptionIds = this.options.map(o => o.id);

    if (this.hasDeletedDisabledDataToggle && !allOptionIds.includes(this.deletedDisabledOptionId)) {
      allOptionIds.push(this.deletedDisabledOptionId);
    }

    return allOptionIds.every(id => this.value.includes(id));
  }

  private emitChanges() {
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
    this.onTouched();
  }
}
